import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, TextInput, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { FONTS } from '../constants/config';
import { PendingDeletionInfo } from '../types';
import { changeRequestsApi } from '../services/apiService';
import { Ionicons } from '@expo/vector-icons';

interface DeletionApprovalBannerProps {
  deletionInfo: PendingDeletionInfo;
  onApproved: () => void;
  onRejected: () => void;
  onViewImpact?: () => void;
  canApprove: boolean;
  canCancel: boolean;
  onCancelled?: () => void;
}

export default function DeletionApprovalBanner({
  deletionInfo,
  onApproved,
  onRejected,
  onViewImpact,
  canApprove,
  canCancel,
  onCancelled,
}: DeletionApprovalBannerProps) {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleApprove = async () => {
    setLoading(true);
    try {
      await changeRequestsApi.approve(deletionInfo.change_request_id);
      onApproved();
    } catch (err) {
      // Error handled by caller
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!showRejectInput) {
      setShowRejectInput(true);
      return;
    }
    setLoading(true);
    try {
      await changeRequestsApi.reject(deletionInfo.change_request_id, rejectReason);
      onRejected();
    } catch (err) {
      // Error handled by caller
    } finally {
      setLoading(false);
      setShowRejectInput(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await changeRequestsApi.cancel(deletionInfo.change_request_id);
      onCancelled?.();
    } catch (err) {
      // Error handled by caller
    } finally {
      setLoading(false);
    }
  };

  const formattedDate = deletionInfo.requested_at
    ? new Date(deletionInfo.requested_at).toLocaleDateString(isRTL ? 'ar-AE' : 'en-US')
    : '';

  return (
    <View style={styles.banner}>
      <View style={[styles.row, isRTL && styles.rowRTL]}>
        <Ionicons name="warning" size={20} color="#EAB308" />
        <View style={styles.textContainer}>
          <Text style={[styles.title, isRTL && styles.rtlText]}>
            {t('deletion.pendingDeletion')}
          </Text>
          <Text style={[styles.subtitle, isRTL && styles.rtlText]}>
            {t('deletion.requestedBy', { name: deletionInfo.requested_by_name })}
            {' '}
            {t('deletion.requestedAt', { date: formattedDate })}
          </Text>
        </View>
      </View>

      {showRejectInput && (
        <TextInput
          style={[styles.rejectInput, isRTL && styles.rtlText]}
          placeholder={t('deletion.reason')}
          value={rejectReason}
          onChangeText={setRejectReason}
          multiline
        />
      )}

      <View style={[styles.actions, isRTL && styles.actionsRTL]}>
        {onViewImpact && (
          <TouchableOpacity style={styles.viewButton} onPress={onViewImpact}>
            <Text style={styles.viewButtonText}>{t('deletion.viewImpact')}</Text>
          </TouchableOpacity>
        )}

        {canCancel && (
          <TouchableOpacity style={styles.cancelButton} onPress={handleCancel} disabled={loading}>
            <Text style={styles.cancelButtonText}>{t('deletion.cancelRequest')}</Text>
          </TouchableOpacity>
        )}

        {canApprove && (
          <>
            <TouchableOpacity
              style={styles.rejectButton}
              onPress={handleReject}
              disabled={loading}
            >
              {loading && showRejectInput ? (
                <ActivityIndicator size="small" color="#DC2626" />
              ) : (
                <Text style={styles.rejectButtonText}>{t('deletion.rejectRequest')}</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.approveButton}
              onPress={handleApprove}
              disabled={loading}
            >
              {loading && !showRejectInput ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.approveButtonText}>{t('deletion.approveRequest')}</Text>
              )}
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#FFFBEB',
    borderWidth: 1,
    borderColor: '#FDE68A',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  rowRTL: {
    flexDirection: 'row-reverse',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 15,
    fontFamily: FONTS.semiBold,
    color: '#854D0E',
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#A16207',
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
    flexWrap: 'wrap',
  },
  actionsRTL: {
    flexDirection: 'row-reverse',
  },
  viewButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#D4D4D4',
  },
  viewButtonText: {
    fontSize: 13,
    color: '#525252',
    fontFamily: FONTS.medium,
  },
  cancelButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#FDE68A',
    backgroundColor: '#FEF9C3',
  },
  cancelButtonText: {
    fontSize: 13,
    color: '#854D0E',
    fontFamily: FONTS.medium,
  },
  approveButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#22C55E',
  },
  approveButtonText: {
    fontSize: 13,
    color: '#fff',
    fontFamily: FONTS.medium,
  },
  rejectButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#FEE2E2',
    borderWidth: 1,
    borderColor: '#FECACA',
  },
  rejectButtonText: {
    fontSize: 13,
    color: '#DC2626',
    fontFamily: FONTS.medium,
  },
  rejectInput: {
    borderWidth: 1,
    borderColor: '#D4D4D4',
    borderRadius: 8,
    padding: 10,
    marginTop: 12,
    minHeight: 60,
    fontFamily: FONTS.regular,
    fontSize: 14,
  },
  rtlText: {
    textAlign: 'right',
  },
});
